import React, { useCallback, useState } from "react";
import { Modal } from "../../../components/Modal";
import styles from "./PhotoPreview.module.scss";

export default function PhotoPreview({
  src,
  name,
  mimeType,
  isImage,
  thumbClassName,
  triggerClassName,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const filename = name || gettext("Photo");
  const lowerName = filename.toLowerCase();
  const lowerSrc = (src || "").toLowerCase();
  const isPdf =
    mimeType === "application/pdf" ||
    lowerName.endsWith(".pdf") ||
    lowerSrc.match(/\.pdf(\?|$)/);
  const canPreviewAsImage =
    isImage === true ||
    mimeType?.startsWith("image/") ||
    lowerName.match(/\.(jpe?g|png|webp)$/) ||
    lowerSrc.match(/\.(jpe?g|png|webp)(\?|$)/);

  const close = useCallback(() => setIsOpen(false), []);

  const openPdfPreview = useCallback(async () => {
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) return;
    previewWindow.opener = null;
    try {
      const response = await fetch(src, { credentials: "same-origin" });
      if (!response.ok) throw new Error("Failed to load PDF");
      const blob = await response.blob();
      const pdfBlob = blob.type === "application/pdf"
        ? blob
        : new Blob([blob], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(pdfBlob);
      previewWindow.location.href = objectUrl;
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      previewWindow.location.href = src;
    }
  }, [src]);

  if (!src) return null;

  if (!canPreviewAsImage) {
    if (isPdf) {
      return (
        <button
          type="button"
          className={`${styles.trigger} ${triggerClassName || ""}`}
          onClick={openPdfPreview}
          aria-label={gettext("Open %(name)s").replace("%(name)s", filename)}
          title={gettext("Open document")}
        >
          <span className={`${styles.fileThumb} ${thumbClassName || ""}`}>
            <i className="fa-solid fa-file-pdf" aria-hidden="true" />
          </span>
        </button>
      );
    }

    return (
      <a
        className={`${styles.trigger} ${triggerClassName || ""}`}
        href={src}
        download={filename}
        aria-label={gettext("Download %(name)s").replace("%(name)s", filename)}
        title={gettext("Download document")}
      >
        <span className={`${styles.fileThumb} ${thumbClassName || ""}`}>
          <i className="fa-solid fa-file" aria-hidden="true" />
        </span>
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`${styles.trigger} ${triggerClassName || ""}`}
        onClick={() => setIsOpen(true)}
        aria-label={gettext("Preview %(name)s").replace("%(name)s", filename)}
        title={gettext("Preview")}
      >
        <img className={thumbClassName} src={src} alt={filename} />
      </button>

      <Modal
        isOpen={isOpen}
        onClose={close}
        title={filename}
        size="md"
        contentClassName={styles.modalContent}
        headerClassName={styles.modalHeader}
        bodyClassName={styles.modalBody}
        headerActions={(
          <a
            className={styles.actionButton}
            href={src}
            download={filename}
            title={gettext("Download")}
            aria-label={gettext("Download")}
          >
            <i className="fa-solid fa-download" aria-hidden="true" />
          </a>
        )}
      >
        <div className={styles.previewFrame}>
          <img className={styles.previewImage} src={src} alt={filename} />
        </div>
      </Modal>
    </>
  );
}
